import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Button, ClickAwayListener, Grow, IconButton, MenuItem, Paper,
  Popper, Select, TextField,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

const CONDITIONS = [
  { symbol: '>', label: 'Greater than' },
  { symbol: '<', label: 'Less than' },
  { symbol: '>=', label: 'At least' },
  { symbol: '<=', label: 'At most' },
  { symbol: '=', label: 'Equals' },
];

export default function MetricFilter({ shortName, condition, value, onChange, onRemove, open, onToggle, onClose }) {
  const [localCond, setLocalCond] = useState(condition || '>');
  const [localVal, setLocalVal] = useState(value || '');
  const anchorRef = useRef(null);

  // Sync from parent when props change
  useEffect(() => {
    setLocalCond(condition || '>');
    setLocalVal(value || '');
  }, [condition, value]);

  const handleClose = (e) => {
    if (anchorRef.current?.contains(e.target)) return;
    // Don't close when clicking inside MUI Select dropdown (rendered via Portal)
    if (
      e.target.closest('[role="listbox"]') ||
      e.target.closest('.MuiPopover-root') ||
      e.target.closest('.MuiModal-root')
    ) return;
    onClose();
  };

  const handleApply = () => {
    if (localVal !== '' && !isNaN(localVal)) {
      onChange(shortName, localCond, localVal);
      onClose();
    }
  };

  const hasValue = condition && value;

  return (
    <>
      <Button
        ref={anchorRef}
        size="small"
        variant={hasValue ? 'contained' : 'outlined'}
        disableElevation
        onClick={onToggle}
        sx={{
          textTransform: 'none', fontSize: '0.8rem', borderRadius: '8px',
          px: 1.2, py: 0.4, minWidth: 0,
          ...(hasValue ? {
            bgcolor: 'primary.main', color: '#fff',
            '&:hover': { bgcolor: 'primary.dark' },
          } : {
            borderColor: 'rgba(0,0,0,0.15)', color: 'text.secondary',
            '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
          }), minWidth: { sm: 'auto', md: 0 },
        }}
      >
        {hasValue ? `${shortName} ${condition} ${value}` : shortName}
      </Button>

      <Popper open={open} anchorEl={anchorRef.current} placement="bottom-start"
        transition disablePortal style={{ zIndex: 20 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
            <Paper sx={{ mt: 0.5, p: 2, boxShadow: 4, borderRadius: '8px', minWidth: 220 }}>
              <ClickAwayListener onClickAway={handleClose}>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Box sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{shortName}</Box>
                    {onRemove && (
                      <IconButton size="small" onClick={() => { onRemove(shortName); onClose(); }}>
                        <CloseRoundedIcon sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5 }}>
                    <Select
                      size="small"
                      value={localCond}
                      onChange={(e) => setLocalCond(e.target.value)}
                      sx={{ minWidth: 80, fontSize: '0.82rem', '& .MuiSelect-select': { py: 0.75 } }}
                    >
                      {CONDITIONS.map(c => (
                        <MenuItem key={c.symbol} value={c.symbol} sx={{ fontSize: '0.82rem' }}>
                          {c.symbol} {c.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <TextField
                      size="small"
                      type="number"
                      value={localVal}
                      onChange={(e) => setLocalVal(e.target.value)}
                      placeholder="Value"
                      onKeyDown={(e) => { if (e.key === 'Enter') handleApply(); }}
                      sx={{
                        flex: 1,
                        '& .MuiInputBase-input': { py: 0.75, fontSize: '0.85rem' },
                      }}
                    />
                  </Box>
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    fullWidth
                    onClick={handleApply}
                    sx={{ textTransform: 'none', fontSize: '0.82rem', borderRadius: '8px' }}
                  >
                    Apply
                  </Button>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}
